import { ImageName } from "@mcleod/images";
import {
    BlurEvent, DataHeader, DataSourceMode, DataSourceModeChangeEvent, Label, LookupModelSearchEvent,
    LookupModelSelectionEvent, Tab, Table, TableContentsChangedEvent, TableRowCreationEvent, DataSourceExecutionEvent,
    ClickEvent, ButtonVariant, Button, Layout, CrudDecorator, Toast, TableRow, Textbox, DataSource,
    DropdownSelectionEvent, TableRowDisplayEvent
} from "@mcleod/components";
import { AutogenLayoutAutoRate } from "./autogen/AutogenLayoutAutoRate";
import { Api, Currency, CurrencySettings, Navigation, UrlUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { LinkedModel, ModelRow } from "@mcleod/core/src/ModelRow";
import { FilterPanel } from "../planning/FilterPanel";

interface AutoRateNavigateProps {
    key?: string;
    odRateId?: string;
    newTab?: boolean;
    additionalProps?: {};
    styleProps?: {};
}

const layoutURL = "lme/dispatch/rates/AutoRate";

export class AutoRate extends AutogenLayoutAutoRate {
    private mcEnabled: boolean;
    private filterPanel: FilterPanel;

    public static navigateTo(props: AutoRateNavigateProps): void {
        Navigation.navigateTo(
            layoutURL + `?mode=${props.key ? DataSourceMode.UPDATE : DataSourceMode.ADD}`
            + (props.key ? `&key=${props.key}` : "")
            + (props.odRateId ? `&odRateId=${props.odRateId}` : ""),
            { newTab: props.newTab, ...props.styleProps },
            { additionalProps: props.additionalProps }
        );
    }

    onLoad(): void | Promise<void> {
        this.styleTab(this.tabAdditionalCharges);
        this.styleTab(this.tabComments);
        this.layoutTableComment.displayCopyOrderFields = false;
        this.layoutTableComment.tableComments.addContentsChangedListener((event: TableContentsChangedEvent) => {
            this.tableCommentOnContentsChanged(event)
        });
        this.layoutTableComment.tableComments.addRowCreateListener((event: TableRowCreationEvent) => {
            this.tableCommentOnRowCreate(event)
        });
        const result = UrlUtil.getPropsFromUrl(window.location.href);
        if (result?.odRateId) {
            this.addLayoutDataLoadListener(() => {
                const row = this.layoutOrigDestRate.tableRates.rows.find(row => row.data.get('id') === result.odRateId);
                if (row) {
                    row.showEditLayout();
                }
            });
        }
    }

    public override doAfterDataHeaderSetup(dataHeader: DataHeader): void {
        dataHeader.promptToSaveChanges();
        dataHeader.showDelete = true;
        const copyButton = new Button({
            imageName: ImageName.copy,
            variant: ButtonVariant.round,
            tooltip: "Copy rate",
            padding: 4,
            margin: 0
        });
        dataHeader.addLeftTools(copyButton)
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.color = "subtle.light";
            tab.headingLabel.setProps({ color: "default", fontBold: true, fillRow: false })
            if (tab["countLabel"] == null) {
                tab["countLabel"] = new Label({
                    fontBold: true, text: "0", fillRow: true,
                    color: "primary", rowBreak: false, marginLeft: 5
                });
                tab.heading.insert(tab["countLabel"], 1);
            }
        }
    }

    /** This is an event handler for the afterModeChange event of sourceRate. */
    sourceRateAfterModeChange(event: DataSourceModeChangeEvent) {
        const isSearch = event.dataSource.mode === DataSourceMode.SEARCH;
        this.layoutOrigDestRate.buttonAdd.visible = !isSearch;
        this.layoutOrigDestRate.buttonSearch.visible = isSearch;
        this.textboxRateNumber.visible = isSearch;

        if (event.dataSource.mode === DataSourceMode.UPDATE) {
            this.mainDataSource.addHasChangedComponent(this.buttonCopy);
            if (this.activeRow != null) {
                this.buttonCopy.doOnDataSourceChanged = (dataSource: DataSource, changesPresent: boolean) => {
                    this.buttonCopy.enabled = !changesPresent;
                }
            }
            this.buttonCopy.visible = true;
        } else {
            this.buttonCopy.visible = false;
        }
        if (event.oldMode === DataSourceMode.ADD && event.newMode === DataSourceMode.UPDATE) {
            const childLinks: LinkedModel[] = this.sourceRate.activeRow?.data["_child_links"];
            childLinks?.forEach(linkedModel => linkedModel.rows?.forEach(row => row._appending = false));
        }

        if (event.dataSource.mode === DataSourceMode.ADD) {
            this.dataHeader.showSave = false;
        }
    }

    /** This is an event handler for the afterExecution event of sourceRate. */
    sourceRateAfterExecution(event: DataSourceExecutionEvent) {
        const rate = event.data;
        this.layoutOrigDestRate.rateId = rate?.getString('id', null);
        this.layoutOrigDestRate.customerId = rate?.getString('customer_id', null);
    }

    updateTabCountLabel(tab: Tab, table: Table) {
        if (tab != null && tab["countLabel"] != null) {
            tab["countLabel"].text = table.dataSource.rowCount + "";
        }
    }

    /** This is an event handler for the onRowCreate event of tableOtherChargeRate. */
    tableOtherChargeRateOnRowCreate(event: TableRowCreationEvent) {
        this.updateTabCountLabel(this.tabAdditionalCharges, event.getTable());
    }

    /** This is an event handler for the onContentsChanged event of tableOtherChargeRate. */
    tableOtherChargeRateOnContentsChanged(event: TableContentsChangedEvent) {
        this.updateTabCountLabel(this.tabAdditionalCharges, event.getTable());
    }

    /** This is an event handler for the afterExecution event of sourceOtherChargeRate. */
    sourceOtherChargeRateAfterExecution(event: DataSourceExecutionEvent) {
        this.updateTabCountLabel(this.tabAdditionalCharges, this.tableOtherChargeRate);
    }

    tableCommentOnContentsChanged(event: TableContentsChangedEvent) {
        this.updateTabCountLabel(this.tabComments, event.getTable());
    }

    tableCommentOnRowCreate(event: TableRowCreationEvent) {
        this.updateTabCountLabel(this.tabComments, event.getTable());
    }

    sourceCommentsAfterExecution() {
        this.updateTabCountLabel(this.tabComments, this.layoutTableComment.tableComments);
    }

    textboxOrderTypeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = 'Y';
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxTrailerType. */
    textboxTrailerTypeBeforeLookupModelSearch(event) {
        event.filter.applies_to = 'L';
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxTractorType. */
    textboxTractorTypeBeforeLookupModelSearch(event) {
        event.filter.applies_to = 'T';
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxTieredTable. */
    textboxTieredTableBeforeLookupModelSearch(event) {
        event.filter.is_active = 'Y';
        if (CurrencySettings.get().multicurrency_enabled) {
            event.filter.currency_type = this.activeRow?.get('currency');
        }
    }

    /** This is an event handler for the onBlur event of textboxCustomer. */
    textboxCustomerOnBlur(event: BlurEvent) {
        if (this.textboxCustomer.isEmpty()) {
            this.layoutOrigDestRate.customerId = null;
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxCustomer. */
    textboxCustomerOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.layoutOrigDestRate.customerId = event.getValueFromSelectedRow('id');
    }

    /** This is an event handler for the afterDropdownSelection event of textboxCurrency. */
    textboxCurrencyOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (!CurrencySettings.get().multicurrency_enabled || !event.hasSelection()) {
            return;
        }
        const currencyType = this.activeRow.get("currency");
        this.updateAllOtherChargeCurrencies(currencyType);
    }

    private updateAllOtherChargeCurrencies(currencyType: string) {
        for (const tableRow of this.tableOtherChargeRate.rows) {
            const modelRow: ModelRow = tableRow.data;
            if (modelRow.isNull("customer_id")) {
                const amount: Currency = modelRow.getCurrency("amount");
                if (amount) {
                    amount.setCurrencyCode(currencyType);
                    modelRow.updateCurrency("amount", amount);
                }
                modelRow.set("amount_c", currencyType);
                tableRow.displayComponentValues();
            }
        }
        const quickAddData: ModelRow = this.tableOtherChargeRate.quickAddData;
        if (quickAddData && quickAddData.isNull("customer_id")) {
            const amount: Currency = quickAddData.getCurrency("amount");
            if (amount) {
                amount.setCurrencyCode(currencyType);
                quickAddData.updateCurrency("amount", amount);
            }
            quickAddData.set("amount_c", currencyType);
            this.tableOtherChargeRate.quickAddRow?.displayComponentValues();
        }
    }

    /** This is an event handler for the onClick event of buttonCopy. */
    async buttonCopyOnClick(event: ClickEvent) {
        this.buttonCopy.busy = true;
        try {
            const willCopy = await CommonDialogs.showYesNo("Are you sure you want to copy this rate?");
            if (!willCopy) return;

            const result = await Api.post('lme/dispatch/duplicate-rate', { "id": this.activeRow.get('id') });
            const newId = result.data[0]['new_id'];
            const layout = Layout.getLayout(this.layoutName);
            const decorator = new CrudDecorator({
                pseudoNavigateUrl: "/" + this.layoutName + "?mode=update&key=" + newId,
                layout: layout,
                key: newId,
                mode: DataSourceMode.UPDATE,
                headerProps: {
                    showClose: true,
                    showSaveAndClose: true,
                }
            });
            decorator.slideIn({ speed: 200 });
            Toast.showSuccessToast("Rate copied successfully.");
        } catch (e) {
            CommonDialogs.showError(e);
        } finally {
            this.buttonCopy.busy = false;
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxId. */
    textboxIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target);
        const modelRow = tableRow.data as ModelRow;

        if (event.selection) {
            modelRow.set("descr", event.selection.get("descr"));
            modelRow.set("calc_method", event.selection.get("charge_rate_method"));
            switch (event.selection.get("charge_rate_method")) {
                case "D":
                    modelRow.set("units", 0);
                    modelRow.set("rate", event.selection.get("amount"))
                    break;
                case "P":
                    modelRow.set("units", 0)
                    modelRow.set("rate", event.selection.get("rate"))
                    break;
                case "F":
                    modelRow.set("units", 1)
                    modelRow.set("rate", event.selection.get("amount"))
                    break;
                case "C":
                case "T":
                    modelRow.set("rate", event.selection.get("amount"))
                    break;
                default:
                    break;
            }
            this.rateFieldChanged(tableRow);
        }
    }

    textboxCalculationMethodAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            const tableRow = TableRow.getContainingTableRow(event.target)
            this.rateFieldChanged(tableRow);
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxCustomerId on the other charge rate table. */
    textboxCustomerIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (!CurrencySettings.get().multicurrency_enabled) {
            return;
        }
        const tableRow = TableRow.getContainingTableRow(event.target);
        const modelRow: ModelRow = tableRow.data as ModelRow;
        let currencyType: string;
        if (event.hasSelection()) {
            currencyType = event.selection.get("currency_type");
        } else {
            currencyType = this.activeRow?.get("currency") || CurrencySettings.getTransactionalCurrency();
        }
        const amount: Currency = modelRow.getCurrency("amount");
        if (amount) {
            amount.setCurrencyCode(currencyType);
            modelRow.updateCurrency("amount", amount);
        }
        modelRow.set("amount_c", currencyType);
        tableRow.displayComponentValues();
    }

    tableOtherChargeRateOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        this.setEnabledDisabled(tableRow);
    }

    rateFieldChanged(tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        switch (modelRow.get("calc_method")) {
            case "D":
                modelRow.set("units", 0)
                this.calculateNonPercentRate(modelRow)
                break;
            case "P":
                if (modelRow.get("units") != null && modelRow.get("rate") != null) {
                    modelRow.updateCurrency("amount", modelRow.get("units") * (1 / modelRow.get("rate")))
                } else {
                    modelRow.updateCurrency("amount", 0);
                }
                break;
            case "F":
                if (modelRow.get("units") == null) {
                    modelRow.set("units", 1);
                }
                this.calculateNonPercentRate(modelRow)
                break;
            case "C":
            case "T":
                modelRow.set("units", 0);
                this.calculateNonPercentRate(modelRow)
                break;
            default:
                break;
        }
        if (CurrencySettings.get().multicurrency_enabled) {
            this.setOtherChargeCurrency(modelRow);
        }
        this.setEnabledDisabled(tableRow)
        tableRow.displayComponentValues()
    }

    private setOtherChargeCurrency(modelRow: ModelRow) {
        const currencyType = this.resolveOtherChargeCurrency(modelRow);
        if (currencyType) {
            const amount: Currency = modelRow.getCurrency("amount");
            if (amount) {
                amount.setCurrencyCode(currencyType);
                modelRow.updateCurrency("amount", amount);
            }
            modelRow.set("amount_c", currencyType);
        }
    }

    private resolveOtherChargeCurrency(modelRow: ModelRow): string {
        if (!modelRow.isNull("amount_c")) {
            return modelRow.getString("amount_c");
        }
        const rateCurrency = this.activeRow.get("currency");
        if (rateCurrency) {
            return rateCurrency;
        }
        return CurrencySettings.getTransactionalCurrency();
    }

    setEnabledDisabled(tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        const textBoxUnits = tableRow.findComponentById("textboxUnits") as Textbox;
        switch (modelRow.get("calc_method")) {
            case "D":
                textBoxUnits.enabled = false;
                break;
            case "P":
                textBoxUnits.enabled = false;
                break;
            case "F":
                textBoxUnits.enabled = true;
                break;
            case "C":
            case "T":
                textBoxUnits.enabled = false;
                break;
            default:
                break;
        }
    }

    calculateNonPercentRate(modelRow: ModelRow) {
        modelRow.updateCurrency("amount", modelRow.get("units") * modelRow.get("rate"))
    }

    textboxRateFieldOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        const tableRow = TableRow.getContainingTableRow(textbox);
        this.rateFieldChanged(tableRow);
    }
}
